/**
 * Concept module — redesigned (iter 10) with sub-tabs.
 *
 * Sub-tabs:
 *  01 Identidad — company name, idea, differentiators, competitive advantage.
 *  02 M·V·V — mission, vision, values.
 *  03 Forma legal — legal form, location.
 *  04 Socios — partners / equity.
 */
import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Fingerprint,
  Compass,
  Scale,
  Users as UsersIcon,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = {
  company_name: "",
  idea: "",
  differentiators: "",
  competitive_advantage: "",
  mission: "",
  vision: "",
  values: "",
  legal_form: "",
  location: "",
  partners: [],
};

export default function ConceptModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("identity");
  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const set = (key, val) => setV((prev) => ({ ...prev, [key]: val }));
  const addPartner = () =>
    setV((p) => ({ ...p, partners: [...(p.partners || []), { name: "", role: "", equity: 0 }] }));
  const updatePartner = (i, k, val) =>
    setV((p) => ({ ...p, partners: p.partners.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const removePartner = (i) =>
    setV((p) => ({ ...p, partners: p.partners.filter((_, idx) => idx !== i) }));

  const equitySum = (v.partners || []).reduce((s, p) => s + (parseFloat(p.equity) || 0), 0);

  const ctx = { project_name: project?.name, project_description: project?.description, company_name: v.company_name, idea: v.idea, differentiators: v.differentiators };
  const suggestions = lang === "es"
    ? [
        "Sugiere 3 ideas diferenciadoras para este negocio",
        "Redacta una ventaja competitiva sólida",
        "Propón la forma jurídica más adecuada y por qué",
        "Sugiere un buen nombre de empresa",
        "Redacta misión, visión y valores",
      ]
    : [
        "Suggest 3 differentiators for this business",
        "Draft a strong competitive advantage statement",
        "Recommend the best legal form and why",
        "Suggest a good company name",
        "Draft mission, vision and values",
      ];

  const tabs = [
    { key: "identity", label: lang === "es" ? "Identidad"   : "Identity",   icon: Fingerprint },
    { key: "mvv",      label: lang === "es" ? "Misión · Visión · Valores" : "Mission · Vision · Values", icon: Compass },
    { key: "legal",    label: lang === "es" ? "Forma legal" : "Legal form", icon: Scale },
    { key: "partners", label: lang === "es" ? "Socios"      : "Partners",   icon: UsersIcon },
  ];

  return (
    <>
      <SectionCard
        title={t("module.concept")}
        subtitle={lang === "es" ? "El ADN de tu empresa: identidad, propósito y estructura." : "Your company DNA: identity, purpose and structure."}
        action={<div className="flex items-center gap-3"><AIBadge /><AIAssistant module="concept" context={ctx} suggestions={suggestions} /></div>}
        testId="concept-section"
      >
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="concept-subtab" />

        {tab === "identity" && (
          <div data-testid="concept-tab-identity" className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <FieldLabel>{t("concept.company_name")}</FieldLabel>
                <TextInput value={v.company_name} onChange={(x) => set("company_name", x)} testId="concept-company-name-input" />
              </div>
              <div className="md:col-span-2">
                <FieldLabel hint={lang === "es" ? "3-4 frases" : "3-4 sentences"}>{t("concept.idea")}</FieldLabel>
                <TextArea value={v.idea} onChange={(x) => set("idea", x)} rows={5} testId="concept-idea-textarea" />
              </div>
              <div>
                <FieldLabel>{t("concept.differentiators")}</FieldLabel>
                <TextArea value={v.differentiators} onChange={(x) => set("differentiators", x)} rows={6} testId="concept-differentiators-textarea" />
              </div>
              <div>
                <FieldLabel>{t("concept.competitive_advantage")}</FieldLabel>
                <TextArea value={v.competitive_advantage} onChange={(x) => set("competitive_advantage", x)} rows={6} testId="concept-advantage-textarea" />
              </div>
            </div>
          </div>
        )}

        {tab === "mvv" && (
          <div data-testid="concept-tab-mvv" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Misión: el por qué (presente). Visión: el adónde (futuro). Valores: el cómo (cultura)."
                : "Mission: the why (present). Vision: the where to (future). Values: the how (culture)."}
            </p>
            <div className="grid md:grid-cols-3 gap-5">
              <div className="border-l-2 border-[#2563EB] bg-white rounded-sm p-5">
                <Overline><span className="text-[#2563EB]">{t("concept.mission")}</span></Overline>
                <TextArea value={v.mission} onChange={(x) => set("mission", x)} rows={8} testId="concept-mission-textarea" />
              </div>
              <div className="border-l-2 border-[#B89154] bg-white rounded-sm p-5">
                <Overline><span className="text-[#B89154]">{t("concept.vision")}</span></Overline>
                <TextArea value={v.vision} onChange={(x) => set("vision", x)} rows={8} testId="concept-vision-textarea" />
              </div>
              <div className="border-l-2 border-[#3A5A6B] bg-white rounded-sm p-5">
                <Overline><span className="text-[#3A5A6B]">{t("concept.values")}</span></Overline>
                <TextArea value={v.values} onChange={(x) => set("values", x)} rows={8} testId="concept-values-textarea" />
              </div>
            </div>
          </div>
        )}

        {tab === "legal" && (
          <div data-testid="concept-tab-legal" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Forma jurídica (S.L., S.A., autónomo…) y ubicación legal de la empresa."
                : "Legal form (LLC, Inc., self-employed…) and registered address."}
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <FieldLabel>{t("concept.legal_form")}</FieldLabel>
                <TextInput value={v.legal_form} onChange={(x) => set("legal_form", x)} placeholder="Sociedad Limitada (S.L.)" testId="concept-legal-form-input" />
              </div>
              <div>
                <FieldLabel>{t("concept.location")}</FieldLabel>
                <TextInput value={v.location} onChange={(x) => set("location", x)} placeholder="Madrid, España" testId="concept-location-input" />
              </div>
            </div>
          </div>
        )}

        {tab === "partners" && (
          <div data-testid="concept-tab-partners" className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Overline>{t("concept.partners")}</Overline>
                <p className="text-xs text-[#8A8886] mt-1">
                  {lang === "es" ? "La suma de equity debe ser 100%" : "Equity must sum to 100%"}
                </p>
              </div>
              <SecondaryButton onClick={addPartner} testId="add-partner-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("concept.add_partner")}
              </SecondaryButton>
            </div>
            {(v.partners || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                {lang === "es" ? "Sin socios. Añade el primero." : "No partners yet. Add the first one."}
              </div>
            ) : (
              <div className="space-y-3">
                {v.partners.map((p, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-3 items-end border border-[#E8E6E1] rounded-sm p-4 bg-white" data-testid={`partner-row-${i}`}>
                    <div>
                      <FieldLabel>{t("concept.partner_name")}</FieldLabel>
                      <TextInput value={p.name} onChange={(x) => updatePartner(i, "name", x)} testId={`partner-${i}-name`} />
                    </div>
                    <div>
                      <FieldLabel>{t("concept.partner_role")}</FieldLabel>
                      <TextInput value={p.role} onChange={(x) => updatePartner(i, "role", x)} testId={`partner-${i}-role`} />
                    </div>
                    <div>
                      <FieldLabel>{t("concept.partner_equity")}</FieldLabel>
                      <TextInput type="number" value={p.equity} onChange={(x) => updatePartner(i, "equity", parseFloat(x) || 0)} testId={`partner-${i}-equity`} />
                    </div>
                    <button onClick={() => removePartner(i)} className="text-[#8A8886] hover:text-[#9E2A2B] transition-colors pb-3" data-testid={`partner-${i}-remove`}>
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
                <div
                  className={`text-sm font-mono pt-3 border-t border-[#E8E6E1] flex justify-between ${
                    Math.abs(equitySum - 100) < 0.01 ? "text-[#2563EB]" : "text-[#9E2A2B]"
                  }`}
                  style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                >
                  <span>{lang === "es" ? "Σ Equity" : "Σ Equity"}</span>
                  <span>{equitySum.toFixed(2)}%</span>
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </>
  );
}
